import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowLeft, Gift, X, ShieldCheck, Loader2 } from "lucide-react";

import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { useCart } from "../../lib/CartContext";
import { useProducts } from "../../lib/ProductsContext";
import { optimizeImg } from "../../lib/img";
import { useCartFlow } from "./CartLayout";

// Sağlayıcı id'sinden (backend'in döndürdüğü) resmi logo — panelde yeni bir
// sağlayıcı eklenirse burada da bir satır eklenmesi yeterli. Logolar
// sağlayıcıların kendi resmi marka paketlerinden alındı (bkz. public/
// payment-logos/).
const PROVIDER_STYLE = {
  paytr:  { logo: "/payment-logos/paytr.png", height: 22 },
  iyzico: { logo: "/payment-logos/iyzico-checkout.svg", height: 34 },
};

export default function CartReviewStep() {
  const { cart: items, addToCart, submitOrder, initiatePayment, submittingOrder } = useCart();
  const { products, categories } = useProducts();
  const navigate = useNavigate();
  const { form, resetForm } = useCartFlow();
  // Sipariş onay adımına gelindiğinde hediye/ek ürün önerisini bir kere,
  // otomatik açılan bir pencere olarak göster — kullanıcı kapatırsa bir daha
  // kendiliğinden açılmaz, aşağıdaki satır-içi bölüm hâlâ erişilebilir kalır.
  const [upsellModalOpen, setUpsellModalOpen] = useState(true);
  // İşletmenin birden fazla aktif ödeme sağlayıcısı varsa (ör. hem PayTR hem
  // iyzico), backend hangisiyle devam edileceğini sormamızı ister — sipariş
  // zaten oluşturulmuş olur, yalnızca ödeme adımı bu seçimi bekler.
  const [providerChoice, setProviderChoice] = useState(null); // { orderId, orderToken, orderNumber, providers }
  const [choosingProvider, setChoosingProvider] = useState(false);
  // KRİTİK HATA DÜZELTMESİ: submitOrder() başarılı olunca sepeti anında
  // boşaltıyor (CartContext.jsx). Bu component `items` (=cart) uzunluğuna
  // bakıp "sepet boşsa /sepet'e geri dön" diye bir useEffect çalıştırıyordu
  // — sipariş zaten oluşturulmuş, ödeme yöntemi seçim penceresi TAM AÇILACAKKEN
  // bu useEffect devreye girip kullanıcıyı sessizce boş sepete geri
  // fırlatıyordu (ne seçim penceresi ne ödeme ekranı hiç görünmüyordu).
  // orderSubmitted true olduğu sürece bu yönlendirme devre dışı bırakılır.
  const [orderSubmitted, setOrderSubmitted] = useState(false);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);

  const requiredFilled = form.name && form.phone && form.il && form.ilce && form.addressDetail && form.deliveryDate;

  useEffect(() => {
    if (orderSubmitted) return;
    if (items.length === 0) { navigate("/sepet", { replace: true }); return; }
    if (!requiredFilled) navigate("/sepet/teslimat", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, requiredFilled, navigate, orderSubmitted]);

  // Ek ürün (aksesuar/çikolata vb.) önerileri — kategori adı bu kelimeleri
  // içeren kategorilerdeki ürünler burada "eklemek ister misiniz?" olarak
  // gösterilir. Panelde henüz böyle bir kategori/ürün yoksa bölüm gösterilmez.
  const upsellProducts = useMemo(() => {
    const upsellCatIds = new Set(
      categories.filter((c) => /çikolata|aksesuar|hediye/i.test(c.name)).map((c) => c.id)
    );
    const inCart = new Set(items.map((i) => i.id));
    return products.filter((p) => upsellCatIds.has(p.cat) && !inCart.has(p.id)).slice(0, 4);
  }, [products, categories, items]);

  if ((items.length === 0 || !requiredFilled) && !orderSubmitted) return null;

  const finalizeOrder = async () => {
    const result = await submitOrder({
      name: form.name,
      phone: form.phone,
      email: form.email,
      il: form.il,
      ilce: form.ilce,
      addressDetail: form.addressDetail,
      note: form.note,
      senderName: form.senderName,
      deliveryDate: form.deliveryDate,
      deliveryStart: form.deliveryStart,
      deliveryEnd: form.deliveryEnd,
    });
    if (!result) return;
    const { orderId, orderNumber, orderToken } = result;

    // Sepet submitOrder() içinde hemen boşaltılıyor — bu satırdan sonra
    // `items` bir sonraki render'da [] olacak. orderSubmitted=true, yukarıdaki
    // "sepet boşsa geri yönlendir" useEffect'ini ve early-return'ü devre dışı
    // bırakır ki ödeme yöntemi seçim penceresi/ödeme ekranı gerçekten görünsün.
    setOrderSubmitted(true);
    resetForm();

    // İşletme online ödemeyi aktif ettiyse ödeme adımına, etmediyse doğrudan
    // teşekkür sayfasına yönlendir — sipariş her durumda zaten alındı.
    const payment = await initiatePayment(orderId, orderToken);
    if (payment?.needsSelection) {
      // Birden fazla aktif sağlayıcı var — müşteriye seçim sunulur, henüz
      // hiçbir sağlayıcı başlatılmadı.
      setProviderChoice({ orderId, orderToken, orderNumber, providers: payment.providers });
      return;
    }
    if (payment?.iframeUrl) {
      navigate("/sepet/odeme", { state: { iframeUrl: payment.iframeUrl, orderId, orderNumber, provider: payment.provider } });
    } else {
      navigate("/sepet/tesekkurler");
    }
  };

  const chooseProvider = async (providerId) => {
    if (!providerChoice) return;
    setChoosingProvider(true);
    const { orderId, orderToken, orderNumber } = providerChoice;
    const payment = await initiatePayment(orderId, orderToken, providerId);
    setChoosingProvider(false);
    if (payment?.iframeUrl) {
      navigate("/sepet/odeme", { state: { iframeUrl: payment.iframeUrl, orderId, orderNumber, provider: payment.provider ?? providerId } });
    } else {
      // Seçilen sağlayıcı başlatılamadıysa bile sipariş zaten alınmıştır —
      // kapıda ödeme/manuel takip akışına yumuşak geçiş yapılır.
      navigate("/sepet/tesekkurler");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2 space-y-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Siparişi Onayla</h1>
          <p className="text-sm text-[#888888]">Her şeyi kontrol edin, hazırsanız siparişinizi tamamlayın.</p>
        </div>

        <div className="space-y-3" data-testid="review-items">
          {items.map((item) => (
            <div key={item.id} className="flex gap-4 p-4 bg-[#F8F8F8] rounded-2xl" data-testid={`review-item-${item.id}`}>
              <img
                src={optimizeImg(item.image, 160)}
                alt={item.name}
                width="64"
                height="64"
                loading="lazy"
                decoding="async"
                className="w-16 h-16 rounded-xl object-cover shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-serif text-sm font-medium text-[#222222] line-clamp-1 mb-1">{item.name}</h4>
                <div className="text-xs text-[#888888]">{item.qty} adet</div>
                <div className="text-[#A85C34] font-semibold text-sm">{(item.price * item.qty).toLocaleString("tr-TR")}₺</div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-[#EAEAEA] p-5 space-y-2 text-sm text-[#555555]" data-testid="review-delivery-summary">
          <h3 className="font-serif text-base text-[#222222] mb-2">Teslimat Bilgileri</h3>
          <p><span className="font-medium text-[#222222]">Alıcı:</span> {form.name}</p>
          <p><span className="font-medium text-[#222222]">Telefon:</span> {form.phone}</p>
          {form.email && <p><span className="font-medium text-[#222222]">E-posta:</span> {form.email}</p>}
          <p><span className="font-medium text-[#222222]">Adres:</span> {form.addressDetail}, {form.ilce} / {form.il}</p>
          <p>
            <span className="font-medium text-[#222222]">Teslimat:</span>{" "}
            {form.deliveryDate && new Date(form.deliveryDate + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "long" })}
            {form.deliveryStart && form.deliveryEnd ? `, ${form.deliveryStart}–${form.deliveryEnd}` : ""}
          </p>
          {form.senderName && <p><span className="font-medium text-[#222222]">Gönderen:</span> {form.senderName}</p>}
          {form.note && <p><span className="font-medium text-[#222222]">Not:</span> {form.note}</p>}
          <button
            type="button"
            onClick={() => navigate("/sepet/teslimat")}
            className="text-xs text-[#A85C34] font-medium hover:underline pt-1"
          >
            Bilgileri düzenle
          </button>
        </div>

        {/* Modal kapatıldıktan sonra öneriler kaybolmasın diye burada da
            görünür kalır — modal AÇIKKEN aynı içeriğin iki kez görünmemesi
            için yalnızca modal kapalıyken render edilir. */}
        {!upsellModalOpen && upsellProducts.length > 0 && (
          <div data-testid="cart-upsell-list">
            <h3 className="font-serif text-base text-[#222222] mb-3">Eklemek İster misiniz?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {upsellProducts.map((p) => (
                <div key={p.id} className="flex gap-3 p-3 bg-[#F8F8F8] rounded-2xl items-center" data-testid={`upsell-item-${p.id}`}>
                  <img
                    src={optimizeImg(p.image, 160)}
                    alt={p.name}
                    width="56"
                    height="56"
                    loading="lazy"
                    decoding="async"
                    className="w-14 h-14 rounded-xl object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-serif text-xs font-medium text-[#222222] line-clamp-1">{p.name}</h4>
                    <div className="text-[#A85C34] font-semibold text-xs">{p.price.toLocaleString("tr-TR")}₺</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => addToCart(p)}
                    aria-label={`${p.name} sepete ekle`}
                    className="w-8 h-8 rounded-full border border-[#A85C34] text-[#A85C34] hover:bg-[#A85C34] hover:text-white transition-colors shrink-0 flex items-center justify-center"
                    data-testid={`upsell-add-${p.id}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="lg:col-span-1">
        <div className="lg:sticky lg:top-8 rounded-2xl border border-[#EAEAEA] p-6 space-y-4">
          <h2 className="font-serif text-lg">Sipariş Özeti</h2>

          <div className="flex items-baseline justify-between border-t border-[#EAEAEA] pt-4">
            <span className="text-[#555555]">Toplam</span>
            <span className="font-serif text-2xl font-bold text-[#222222]" data-testid="review-total">
              {subtotal.toLocaleString("tr-TR")}₺
            </span>
          </div>
          <button
            type="button"
            onClick={finalizeOrder}
            disabled={submittingOrder}
            className="w-full bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full px-6 py-4 font-medium transition-colors disabled:opacity-60"
            data-testid="review-submit"
          >
            {submittingOrder ? "Gönderiliyor..." : "Siparişi Onayla"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/sepet/teslimat")}
            className="w-full flex items-center justify-center gap-2 text-sm text-[#888888] hover:text-[#222222] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Teslimat Bilgilerine Dön
          </button>
        </div>
      </div>

      <Dialog open={upsellModalOpen && upsellProducts.length > 0} onOpenChange={setUpsellModalOpen}>
        <DialogContent className="max-w-md bg-white border-0 rounded-3xl p-0 overflow-hidden" data-testid="upsell-modal">
          <div className="p-6">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-full bg-[#F5E9DD] flex items-center justify-center shrink-0">
                <Gift className="w-4 h-4 text-[#A85C34]" />
              </div>
              <DialogTitle asChild>
                <h3 className="font-serif text-xl text-[#222222]">Eklemek İster misiniz?</h3>
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-[#888888] mb-5">
              Siparişinizi hediye kutusu veya çikolata ile tamamlayabilirsiniz.
            </DialogDescription>

            <div className="flex flex-col gap-2.5 max-h-[50vh] overflow-y-auto">
              {upsellProducts.map((p) => (
                <div key={p.id} className="flex gap-3 p-3 bg-[#F8F8F8] rounded-2xl items-center" data-testid={`upsell-modal-item-${p.id}`}>
                  <img
                    src={optimizeImg(p.image, 160)}
                    alt={p.name}
                    width="56"
                    height="56"
                    loading="lazy"
                    decoding="async"
                    className="w-14 h-14 rounded-xl object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-serif text-sm font-medium text-[#222222] line-clamp-1">{p.name}</h4>
                    <div className="text-[#A85C34] font-semibold text-xs">{p.price.toLocaleString("tr-TR")}₺</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => addToCart(p)}
                    className="px-3.5 h-9 rounded-full border border-[#A85C34] text-[#A85C34] hover:bg-[#A85C34] hover:text-white transition-colors shrink-0 inline-flex items-center gap-1.5 text-xs font-medium"
                    data-testid={`upsell-modal-add-${p.id}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Ekle
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setUpsellModalOpen(false)}
              className="w-full mt-5 flex items-center justify-center gap-2 text-sm text-[#888888] hover:text-[#222222] transition-colors"
              data-testid="upsell-modal-dismiss"
            >
              <X className="w-3.5 h-3.5" />
              Hayır, teşekkürler
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ödeme Yöntemi Seçimi — yalnızca işletmenin birden fazla aktif ödeme
          sağlayıcısı varsa görünür (tek sağlayıcı aktifse bu adım hiç
          gösterilmeden doğrudan ödeme sayfasına geçilir). */}
      <Dialog open={!!providerChoice} onOpenChange={(open) => { if (!open && !choosingProvider) setProviderChoice(null); }}>
        <DialogContent className="max-w-sm bg-white border-0 rounded-3xl p-0 overflow-hidden" data-testid="provider-choice-modal">
          <div className="p-6">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-full bg-[#F5E9DD] flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4 h-4 text-[#A85C34]" />
              </div>
              <DialogTitle asChild>
                <h3 className="font-serif text-xl text-[#222222]">Ödeme Yöntemi Seçin</h3>
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-[#888888] mb-5">
              Siparişiniz alındı — güvenli ödeme için bir yöntem seçin.
            </DialogDescription>

            <div className="flex flex-col gap-2.5">
              {(providerChoice?.providers || []).map((p) => {
                const style = PROVIDER_STYLE[p.id] || {};
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={choosingProvider}
                    onClick={() => chooseProvider(p.id)}
                    className="flex items-center justify-between gap-3 p-4 rounded-2xl border border-[#EAEAEA] hover:border-[#A85C34] transition-colors disabled:opacity-60"
                    data-testid={`provider-choice-${p.id}`}
                  >
                    {style.logo ? (
                      <img src={style.logo} alt={p.label} style={{ height: style.height || 26 }} className="object-contain" />
                    ) : (
                      <span className="font-medium text-sm text-[#222222]">{p.label}</span>
                    )}
                    {choosingProvider ? (
                      <Loader2 className="w-4 h-4 animate-spin text-[#A85C34]" />
                    ) : (
                      <span className="text-xs text-[#888888]">ile devam et</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
